import { ReactNode, useEffect, useRef, useState } from 'react'
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react'
import clsx from 'clsx'
import { useSearchParams } from 'next/navigation'

import Button from 'shared/ui/Button'
import { Collapse } from 'shared/ui/Collapse'
import type { CollapseProps } from 'shared/ui/Collapse/Collapse'
import { KEYBOARD_KEYS } from 'shared/ui/types'

import { FILTER_NAMES } from '../utils'

import styles from './HorizontalExpendablePanel.module.scss'

const StyledCollapse = ({ className, ...props }: CollapseProps) => (
  <Collapse className={clsx(styles.collapse, className)} {...props} />
)

interface ExpandablePanelProps {
  id?: string
  title: string
  children: ReactNode | string
  disabled?: boolean
  expanded?: boolean
  isActive?: boolean
}

const HorizontalExpendablePanel = ({
  id,
  title,
  children,
  disabled,
  isActive,
  expanded = false
}: ExpandablePanelProps) => {
  const [open, setOpen] = useState(expanded)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const searchParams = useSearchParams()

  const toggleOpen = () => setOpen(prev => !prev)

  const handleClickOutside = (event: MouseEvent) => {
    if (
      wrapperRef.current &&
      !wrapperRef.current.contains(event.target as Node)
    ) {
      setOpen(false)
    }
  }

  useEffect(() => {
    if (open && title !== (FILTER_NAMES.Type as string)) {
      setOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  const KeydownExit = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === KEYBOARD_KEYS.ESCAPE) {
      e.preventDefault()
      e.stopPropagation()
      setOpen(false)
    }
  }

  return (
    <div
      ref={wrapperRef}
      className={clsx(styles.wrapper, { [styles.disabled]: disabled })}
      {...{ disabled }}
    >
      <Button
        id={id}
        onClick={toggleOpen}
        variant='neutralOutline'
        active={isActive}
        endIcon={open ? <IconChevronUp /> : <IconChevronDown />}
      >
        <h2
          className={clsx(styles.panelTitle, 'expandable-panel_title')}
          id={`${title}-header`}
          aria-expanded={open}
          aria-controls={`${title}-collapse`}
        >
          {title}
        </h2>
      </Button>
      <StyledCollapse
        open={open}
        onKeyDown={KeydownExit}
        id={`${title}-collapse`}
        aria-labelledby={`${title}-header`}
      >
        {/* `open` is also rendered as a DOM attribute, as before */}
        <div
          className={clsx(styles.panelContent, { [styles.open]: open })}
          {...{ open }}
        >
          {children}
        </div>
      </StyledCollapse>
    </div>
  )
}

export default HorizontalExpendablePanel
